// Completion capture, mirroring fi-quizz/src/capture.ts so both quizzes write
// the same cookie shape and hit the same server endpoint.
//
// On completion:
//   1. the fi_quiz cookie, unconditionally - the profile the shop reads for PDP
//      personalisation and checkout prefill
//   2. a client-side _cio.identify and POST /api/ecommerce/mark-quiz-completed,
//      ONLY when an email was given. No email is no consent signal, so a skipped
//      email step captures no lead.

import { webApiUrl } from '../../lib/fi-api/apiUtils';
import { quizQuestionPage } from '../../lib/analytics/events';
import { SIGNUP_SOURCE } from '../Footer/JoinEmailForm';
import { ProductId } from './rules';
import { QuizAnswers } from './steps';

const QUIZ_COOKIE_NAME = 'fi_quiz';
const QUIZ_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
const MARK_QUIZ_COMPLETED_PATH = '/api/ecommerce/mark-quiz-completed';

/** Version 3 is fi-quizz's answer shape; readers branch on it. This flow writes the same one. */
const QUIZ_PAYLOAD_VERSION = 3;

/** fi-quizz's recommendation vocabulary, so consumers see one vocab from either quiz. */
const COOKIE_RECOMMENDATION: Record<ProductId, 'mini' | 's3'> = { mini: 'mini', series3plus: 's3' };

interface QuizCompletionPayload {
  event: 'quiz_completed';
  version: number;
  source: 'hardware_first_shop';
  completed: true;
  pet: { type: 'dog'; name: null; breed: null; size: string | null };
  signals: {
    age: string | null;
    residence: string | null;
    exercise: string[];
    spend_time: string[];
    importance: string[];
  };
  lead: { email: string | null; consent: boolean };
  recommendation: 'mini' | 's3';
}

function buildPayload(answers: QuizAnswers, recommendation: ProductId): QuizCompletionPayload {
  const email = answers.email?.trim() || null;

  return {
    event: 'quiz_completed',
    version: QUIZ_PAYLOAD_VERSION,
    source: 'hardware_first_shop',
    completed: true,
    pet: { type: 'dog', name: null, breed: null, size: answers.size },
    signals: {
      age: answers.age,
      residence: answers.residence,
      exercise: [...answers.exercise],
      // this flow never asks about cats, but the envelope is shared with fi-quizz
      spend_time: [],
      importance: [...answers.importance],
    },
    lead: { email, consent: email !== null },
    recommendation: COOKIE_RECOMMENDATION[recommendation],
  };
}

function writeQuizCookie(payload: QuizCompletionPayload): void {
  try {
    const parts = [
      `${QUIZ_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}`,
      `max-age=${QUIZ_COOKIE_MAX_AGE_SECONDS}`,
      'path=/',
      'SameSite=Lax',
    ];
    if (window.location.protocol === 'https:') {
      parts.push('Secure');
    }
    document.cookie = parts.join('; ');
  } catch {
    // privacy mode / non-browser: the quiz still works, it just does not persist
  }
}

/** Person attributes reuse the live quiz's names where the concept matches. */
function identifyLead(answers: QuizAnswers, email: string): void {
  const cio = (window as unknown as { _cio?: { identify(payload: Record<string, unknown>): void } })._cio;
  if (!cio) {
    return;
  }

  const attributes: Record<string, unknown> = {
    id: email.toLowerCase(),
    email,
    source: `${SIGNUP_SOURCE}_quiz`,
    created_at: Math.round(Date.now() / 1000),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    'Pet Species': 'DOG',
  };

  if (answers.age) {
    attributes['Pet Age'] = answers.age;
  }
  if (answers.size) {
    attributes['Pet Size'] = answers.size;
  }
  if (answers.residence) {
    attributes['Pet Residence'] = answers.residence;
  }
  if (answers.exercise.length > 0) {
    attributes['Pet Exercise'] = [...answers.exercise].sort().join(', ');
  }
  if (answers.importance.length > 0) {
    attributes['Pet Importance'] = [...answers.importance].sort().join(', ');
  }

  cio.identify(attributes);
}

/**
 * Runs every capture the completed quiz owes. Fire-and-forget: nothing here
 * blocks the result screen, and a failed write never surfaces to the visitor.
 */
export default function captureQuizCompletion(answers: QuizAnswers, recommendation: ProductId): void {
  const payload = buildPayload(answers, recommendation);
  writeQuizCookie(payload);
  quizQuestionPage.quizCompleted({
    recommendation: payload.recommendation,
    source: payload.source,
    version: payload.version,
    hasEmail: payload.lead.consent,
  });

  const email = payload.lead.email;
  if (!email) {
    return;
  }

  identifyLead(answers, email);

  // The server-authoritative write: sets the quiz_completed profile attributes
  // and emits the event. Ad-blocker-proof, unlike the _cio identify above.
  fetch(`${webApiUrl}${MARK_QUIZ_COMPLETED_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, quiz: payload }),
  }).catch(() => {
    // the cookie and the identify already landed; this is the durable copy
  });
}
